import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import {
  FluentProvider,
  Spinner,
  makeStyles,
  tokens,
  type Theme,
} from '@fluentui/react-components';
import { prLightTheme, prDarkTheme, prHighContrastTheme } from '../theme/brand';

const useStyles = makeStyles({
  // FluentProvider paints colorNeutralBackground1 on itself, so anything it
  // does not cover falls back to the browser's white — a white band under a
  // dark tab, most visible while the loading state is short. Filling the
  // viewport keeps the themed surface edge to edge in every state.
  //
  // This MUST stay on an inner element and never move to FluentProvider's own
  // `className`. FluentProvider defaults `applyStylesToPortals` to true, which
  // makes `useThemeClassName()` return the provider root's *entire* className —
  // app-supplied classes included — and `usePortalMountNode` stamps that onto
  // every portal mount node (Combobox listbox, Menu popover, Dialog surface).
  // Those nodes are already `position:absolute; top/left/right:0;
  // z-index:1000000` with an opaque themed background, so a leaked
  // `min-height:100vh` turns each one into a full-viewport panel covering the
  // whole app the moment any dropdown opens. See CLAUDE.md log entry 23.
  //
  // The canvas colour lives here too, and deliberately not on `App`'s own root.
  // FluentProvider paints `colorNeutralBackground1` on itself, so a page that
  // paints `colorNeutralBackground2` over the whole viewport once it mounts
  // makes the tab visibly change shade the moment content appears. Owning both
  // the fill and the colour on the same element removes that boot flash.
  fill: { minHeight: '100vh', backgroundColor: tokens.colorNeutralBackground2 },
});

/** The raw Teams theme name, as `themeFor` below switches on it — exposed so
 * consumers that render outside FluentProvider's reach (a sandboxed iframe's
 * own document, e.g. AttachmentPreviewDialog's docx/xlsx preview) can pick a
 * matching literal palette instead of a Fluent token, which cannot cross that
 * boundary. */
export type TeamsThemeName = 'default' | 'dark' | 'contrast';

interface TeamsContextValue {
  inTeams: boolean;
  context: microsoftTeams.app.Context | null;
  /** Teams SSO token for calling our own API (Bearer). */
  getAuthToken: () => Promise<string>;
  /** Current resolved Teams theme name — kept in sync with `theme` below, both
   * at initial mount and on every live theme change. */
  themeName: TeamsThemeName;
}

const TeamsContext = createContext<TeamsContextValue | null>(null);

export function useTeams(): TeamsContextValue {
  const ctx = useContext(TeamsContext);
  if (!ctx) throw new Error('useTeams must be used inside TeamsProvider');
  return ctx;
}

/**
 * Module scope, not component scope: it captures nothing from the render, so
 * re-creating it per render would needlessly change the context value identity.
 */
const getAuthToken = (): Promise<string> => microsoftTeams.authentication.getAuthToken();

/**
 * The three themes are the Teams ones with the app's own indigo brand ramp
 * injected (`theme/brand.ts`). Neutrals, type ramp and the Teams font stack are
 * unchanged, so the tab still reads as native Teams; high contrast is passed
 * through untouched, because it discards the brand hue by design.
 */
/** Normalizes whatever teams-js hands back to the three names this app
 * distinguishes — the single place both `theme` and `themeName` state derive
 * from, so they can never disagree. */
function normalizeThemeName(name: string | undefined): TeamsThemeName {
  if (name === 'dark') return 'dark';
  if (name === 'contrast') return 'contrast';
  return 'default';
}

function themeFor(name: TeamsThemeName): Theme {
  switch (name) {
    case 'dark':
      return prDarkTheme;
    case 'contrast':
      return prHighContrastTheme;
    default:
      return prLightTheme;
  }
}

export function TeamsProvider({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const [ready, setReady] = useState(false);
  const [inTeams, setInTeams] = useState(false);
  const [context, setContext] = useState<microsoftTeams.app.Context | null>(null);
  const [theme, setTheme] = useState<Theme>(prLightTheme);
  const [themeName, setThemeName] = useState<TeamsThemeName>('default');

  useEffect(() => {
    let cancelled = false;
    // `void`: this IIFE owns its own error handling below, so the promise is
    // deliberately not awaited by the effect (effects cannot be async).
    void (async () => {
      try {
        await microsoftTeams.app.initialize();
        const ctx = await microsoftTeams.app.getContext();
        if (cancelled) return;
        setInTeams(true);
        setContext(ctx);
        const initialName = normalizeThemeName(ctx.app.theme);
        setThemeName(initialName);
        setTheme(themeFor(initialName));
        microsoftTeams.app.registerOnThemeChangeHandler((t) => {
          const name = normalizeThemeName(t);
          setThemeName(name);
          setTheme(themeFor(name));
        });
        void microsoftTeams.app.notifySuccess();
      } catch {
        // Running outside Teams (plain browser during development)
        if (!cancelled) setInTeams(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stable identity: without this every render hands consumers a new object and
  // re-renders the whole tree under the provider.
  const value = useMemo<TeamsContextValue>(
    () => ({ inTeams, context, getAuthToken, themeName }),
    [inTeams, context, themeName],
  );

  // One FluentProvider for the lifetime of the tab, with the loading state
  // swapped *inside* it (#54). Returning a different tree shape while `!ready`
  // put a different element type at this position, so React tore the provider
  // down and mounted a fresh one the moment initialization settled — a style
  // tag rebuild, and a visible flash, right as the Teams theme was applied.
  // A theme change must only ever re-render: never remount.
  return (
    <TeamsContext.Provider value={value}>
      <FluentProvider theme={theme}>
        <div className={styles.fill}>
          {ready ? children : <Spinner label="Loading…" style={{ marginTop: '30vh' }} />}
        </div>
      </FluentProvider>
    </TeamsContext.Provider>
  );
}
